'use client'
import dynamic from 'next/dynamic'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

const ResourcesAside = dynamic(() => import('./ResourcesAside'))
const ResourcesContent = dynamic(() => import('./ResourcesContent'))

type DataObject = Record<string, any>
type ResourcesProps = {
  data: DataObject
  portalId: string
  formId: string
  complianceTypesData: DataObject
  complianceTopicsData: DataObject
  newsLetterOptInFormTitle: DataObject
}

const Resources = ({
  data,
  portalId,
  formId,
  complianceTypesData,
  complianceTopicsData,
  newsLetterOptInFormTitle,
}: ResourcesProps) => {
  const [filterResults, setFilterResults] = useState<any>([])
  const [showNoData, setShowNoData] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [typesData, setTypesData] = useState<string[]>([])
  const [topicsData, setTopicsData] = useState<string[]>([])
  const searchParams = useSearchParams()

  useEffect(() => {
    const types = searchParams.getAll('types')
    const topics = searchParams.getAll('topics')

    setTypesData((prevTypes) => Array.from(new Set([...prevTypes, ...types])))
    setTopicsData((prevTopics) => Array.from(new Set([...prevTopics, ...topics])))
  }, [searchParams])

  const setTypeData = useCallback(
    (value: string) => {
      const typeIndex = typesData.indexOf(value)

      if (typeIndex === -1) {
        setTypesData((prevTypes: string[]) => [...prevTypes, value])
      } else {
        setTypesData((types: string[]) => types.filter((_, index) => index !== typeIndex))
      }
    },
    [typesData],
  )

  const setTopicData = useCallback(
    (value: string) => {
      const topicIndex = topicsData.indexOf(value)

      if (topicIndex === -1) {
        setTopicsData((prevTopics: string[]) => [...prevTopics, value])
      } else {
        setTopicsData((topics: string[]) => topics.filter((_, index) => index !== topicIndex))
      }
    },
    [topicsData],
  )

  useEffect(() => {
    let filteredUniqueData

    if (typesData.length > 0 || topicsData.length > 0) {
      const filteredTypesData = data.items.filter((item: any) => {
        return typesData.includes(item.type)
      })
      const filteredTopicsData = data.items.filter((item: any) => {
        return topicsData.includes(item.topic)
      })

      const mergeData = [...filteredTypesData, ...filteredTopicsData]

      if (typesData.length > 0) filteredUniqueData = filteredTypesData
      if (topicsData.length > 0) filteredUniqueData = filteredTopicsData
      if (typesData.length > 0 && topicsData.length > 0) {
        filteredUniqueData = Array.from(
          new Set(mergeData.filter((obj) => typesData.includes(obj.type) && topicsData.includes(obj.topic))),
        )
      }

      setFilterResults(filteredUniqueData)
    } else {
      setFilterResults(data.items)
    }
  }, [topicsData, typesData, data.items])

  useEffect(() => {
    if (searchText !== '') {
      const filteredData = data.items.filter((item: any) => {
        return item.title[0].text.toLowerCase().indexOf(searchText.toLowerCase()) !== -1
      })

      if (filteredData.length > 0) {
        setShowNoData(false)
        setFilterResults(filteredData)
      } else setShowNoData(true)
    } else {
      setFilterResults(data.items)
      setShowNoData(false)
    }
  }, [searchText, data.items])

  return (
    <section className="relative z-[1] py-10 lg:py-[60px]">
      <div className="container">
        <div className="grid gap-8 lg:grid-cols-8">
          <ResourcesAside
            setSearchText={setSearchText}
            setTypeData={setTypeData}
            setTopicData={setTopicData}
            typesData={typesData}
            topicsData={topicsData}
            portalId={portalId}
            formId={formId}
            complianceTypesData={complianceTypesData}
            complianceTopicsData={complianceTopicsData}
            newsLetterOptInFormTitle={newsLetterOptInFormTitle}
          />

          <ResourcesContent filterResults={filterResults} showNoData={showNoData} showPagination={!showNoData} />
        </div>
      </div>
    </section>
  )
}

export default Resources
